import { useState, useEffect } from 'react';
import { pdf } from '@react-pdf/renderer';
import PdfExternos from '../components/PdfExternos';
import generarLineaCaptura from "../utilities/GenerarLineaCaptura";
import { API_BASE, PINPAD_URL } from "../config";
import { plantelOficialDesdeIdBD } from "../utilities/plantel";
import { apiFetch } from "../utilities/apiFetch";

const useCobroExternos = (user) => {
  const [externos, setExternos] = useState([]);
  const [externoSeleccionado, setExternoSeleccionado] = useState('');
  const [montoManual, setMontoManual] = useState('');
  const [carrito, setCarrito] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [loadingLista, setLoadingLista] = useState(true);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [modalExito, setModalExito] = useState({
    visible: false, nombre: '', monto: 0, autorizacion: '', referencia: ''
  });

  // =========================================================
  // La validación de pendientes vive en useHistorialExternos
  // =========================================================

  useEffect(() => {
    fetchExternos();
  }, [user?.id_plantel]);

  // Efecto para limpiar el carrito y el monto al cambiar de concesionario
  useEffect(() => {
    setCarrito(null);
    setMontoManual('');
  }, [externoSeleccionado]);

  const fetchExternos = async () => {
    setLoadingLista(true);
    try {
      const response = await apiFetch(`${API_BASE}/wMultipagosObtenerExternos.php?id_plantel=${user?.id_plantel}`);
      const data = await response.json();
      if (data.success) {
        setExternos(data.data);
      } else {
        mostrarNotificacion('error', data.message);
      }
    } catch (error) {
      mostrarNotificacion('error', 'No se pudo conectar con el servidor.');
    } finally {
      setLoadingLista(false);
    }
  };

  const mostrarNotificacion = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: '', texto: '' }), 5000);
  };

  const handleAgregarAlCarrito = () => {
    const entidad = externos.find(e => e.matricula === externoSeleccionado);
    if (!entidad) return mostrarNotificacion('error', 'Selecciona un concesionario.');
    if (!montoManual || parseFloat(montoManual) <= 0) return mostrarNotificacion('error', 'Ingresa un monto válido.');

    const claveConcepto = entidad.clave || entidad.clave_concepto || (entidad.tipo === 'CAFETERIA' ? '18' : '19');
    const nombreServicio =
      entidad.tipo === 'CAFETERIA' ? 'PAGO DE CONCESIÓN DE CAFETERÍA'
      : entidad.tipo === 'PAPELERIA' ? 'PAGO DE CONCESIÓN DE PAPELERÍA'
      : (entidad.descripcion || entidad.concepto || 'PAGO DE SERVICIO');

    setCarrito({
      matricula: entidad.matricula,
      nombre: entidad.nombre,
      clave_concepto: claveConcepto,
      descripcion: nombreServicio,
      precio: parseFloat(montoManual),
    });
    mostrarNotificacion('success', 'Concepto listo para cobro.');
  };

  const procesarCobroPinpad = async () => {
    if (!carrito) return;
    setLoading(true);
    try {
      const idPlantelBanco = plantelOficialDesdeIdBD(user?.id_plantel);
      const infoCaptura = generarLineaCaptura(idPlantelBanco, [carrito.clave_concepto], carrito.matricula, carrito.precio);
      
      const referenciaOficial26 = infoCaptura.banorte.lineaDeCaptura.substring(0, 26);
      const sufijoTiempo = Date.now().toString().slice(-4); 
      const referenciaTerminal = referenciaOficial26 + sufijoTiempo;

      const resPinpad = await fetch(PINPAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Monto: carrito.precio, Referencia: referenciaTerminal, Concepto: carrito.descripcion.substring(0, 30) })
      });
      const dataPinpad = await resPinpad.json();

      if (dataPinpad.status === 'Aprobado') {
        const payloadPago = {
          alumno: carrito.matricula,
          cajero: user?.id_usuario,
          plantel: user?.id_plantel, 
          total: carrito.precio,
          detalle: [{ id: carrito.clave_concepto, clave: carrito.clave_concepto, concepto: carrito.descripcion, precio: carrito.precio, cantidad: 1 }], 
          lineaCapturaFinal: referenciaTerminal,  
          referenciaBase: referenciaOficial26,    
          fechaVencimiento: "", 
          autorizacion: dataPinpad.autorizacion 
        };

        const resPago = await apiFetch(`${API_BASE}/wMultipagosGuardarPagoCaja.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadPago)
        });
        const dataPago = await resPago.json();

        if (dataPago.success) {
          setModalExito({ visible: true, nombre: carrito.nombre, monto: carrito.precio, autorizacion: dataPinpad.autorizacion, referencia: referenciaTerminal });
          setCarrito(null);
          setMontoManual('');
          setExternoSeleccionado('');
        } else {
          mostrarNotificacion('error', 'Cobro guardado con errores: ' + (dataPago.message || ''));
        }
      } else {
        mostrarNotificacion('error', `Terminal: ${dataPinpad.mensaje}`);
      }
    } catch (error) {
      mostrarNotificacion('error', 'Error con la Pinpad.');
    } finally {
      setLoading(false); 
    }
  };

  const procesarCobroVentanilla = async () => {
    if (!carrito) return;
    setLoading(true);
    try {
      const idPlantelBanco = plantelOficialDesdeIdBD(user?.id_plantel);
      const infoCaptura = generarLineaCaptura(idPlantelBanco, [carrito.clave_concepto], carrito.matricula, carrito.precio);
      
      // Generamos el folio
      const nuevoFolioTPV = Math.floor(100000 + Math.random() * 900000).toString();

      const payloadIntencion = {
        codigo_alumno: carrito.matricula,
        referencia_banorte: infoCaptura.banorte.lineaDeCaptura,
        referencia_bbva: infoCaptura.bancomer.lineaDeCaptura,
        importe: carrito.precio,
        id_plantel: user?.id_plantel,
        id_cajero: user?.id_usuario,
        conceptos: [{ clave: carrito.clave_concepto, precio: carrito.precio }],
        metodo_esperado: 'VENTANILLA',
        folio_registro: nuevoFolioTPV // <-- Enviamos folio_registro para coincidir con PHP
      };

      const resInt = await fetch(`${API_BASE}/wMultipagosGuardarIntencion.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payloadIntencion)
      });
      const dataInt = await resInt.json();

      if (dataInt.success) {
        mostrarNotificacion('success', 'Generando orden...');
        const blob = await pdf(
          <PdfExternos 
            name={carrito.nombre} 
            matricula={carrito.matricula} 
            idPlantel={idPlantelBanco} 
            nombrePlantel={user?.nombre_plantel} 
            concepto={carrito.descripcion} 
            referenciaBanorte={infoCaptura.banorte.lineaDeCaptura}
            referenciaBBVA={infoCaptura.bancomer.lineaDeCaptura}
            precio={carrito.precio} 
            folioTPV={nuevoFolioTPV}
            fechaVencimiento={infoCaptura.banorte.fechaVencimientoStr}
          />
        ).toBlob();
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = `Ficha_${carrito.matricula}.pdf`;
        document.body.appendChild(link); link.click();
        document.body.removeChild(link); URL.revokeObjectURL(url);

        setCarrito(null); setMontoManual(''); setExternoSeleccionado('');
      } else {
        mostrarNotificacion('error', dataInt.message);
      }
    } catch (error) {
      mostrarNotificacion('error', 'Error al procesar ventanilla.');
    } finally {
      setLoading(false);
    }
  };

  // RETORNAMOS TODO LO NECESARIO PARA LA VISTA
  return {
    externos, externoSeleccionado, setExternoSeleccionado, montoManual, setMontoManual,
    carrito, loadingLista, loading, mensaje, modalExito, setModalExito,
    handleAgregarAlCarrito, procesarCobroPinpad, procesarCobroVentanilla
  };
};

export default useCobroExternos;